import { ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subject, takeUntil } from 'rxjs';
import { toTitleCase } from '../../_shared/_helpers/titlecase';
import { ICartLineInput, IShopifyCart, IShopifyProduct, IShopifyProductVariant, SEO } from '@app/_shared/_models';
import { CartService } from '../../_shared/_services/cart.service';
import { GoogleAnalyticsService } from '../../_shared/_services/google-analytics.service';
import { ProductService } from '../../_shared/_services/product.service';
import { SeoService } from '../../_shared/_services/seo.service';
import { NgxSkeletonLoaderComponent } from 'ngx-skeleton-loader';
import { NgClass, AsyncPipe } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { SubscriptionComponent } from '../../_shared/_components/subscription/subscription.component';
import { MatIcon } from '@angular/material/icon';
import { SimilarProductsComponent } from '../../_shared/_components/similar-products/similar-products.component';

@Component({
    selector: 'app-product',
    templateUrl: './product.component.html',
    styleUrls: ['./product.component.scss'],
    imports: [NgxSkeletonLoaderComponent, NgClass, MatButton, SubscriptionComponent, MatIcon, SimilarProductsComponent, AsyncPipe]
})
export class ProductComponent implements OnInit, OnDestroy {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly productService = inject(ProductService);
  private readonly cartService = inject(CartService);
  private readonly titleService = inject(Title);
  private readonly seoService = inject(SeoService);
  private readonly googleAnalyticsService = inject(GoogleAnalyticsService);
  private readonly router = inject(Router);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  public seo: SEO = {
    image: '',
    metaDescription: '',
    metaTitle: ''
  };
  public variantIndex = 0;
  public handle!: string | null;
  public product$!: Observable<IShopifyProduct | undefined>;
  public amountToAddToCart = 1;
  public isDescriptionExpanded = false;
  public inStockDescription = 'In Stock';
  public amountInCart = 0;

  private cart: IShopifyCart | undefined;
  private product: IShopifyProduct | undefined;
  private selectedSubscription = '';
  private readonly unsubscribe: Subject<undefined> = new Subject<undefined>();

  constructor() {
    this.product$ = this.productService.product$;
  }

  ngOnInit(): void {
    this.activatedRoute.params
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(async (params: any) => {
        const handle = params['name'];
        if (handle) {
          this.handle = handle;
          this.seoService.createCanonicalUrl(`https://mypoolhut.com/products/${handle}`);
          const currentTitle = this.titleService.getTitle();
          if (this.handle) {
            let productTitle = this.handle.replace(/-/g, ' ');
            productTitle = toTitleCase(productTitle);
            this.titleService.setTitle(`${productTitle} | ${currentTitle}`)
            const product = await this.productService.loadProductByHandle(this.handle);

            if (product) {
              this.product = product;
              for (let i = 0; i < product.variants.length; i += 1) {
                if (product.variants[i].inventory > 0) {
                  this.calculateCartAmount();
                  await this.selectVariant(product, i);
                  break;
                }
              }
            } else {
              if (this.router.url?.includes('/products/')) {
                await this.router.navigate(['/products/product-not-found']);
              }
            }
          }
        }
    });

    this.cartService.cart$
      .pipe(
        takeUntil(this.unsubscribe)
      )
      .subscribe((cart: IShopifyCart | undefined) => {
        if (cart) {
          this.cart = cart;
          this.calculateCartAmount();
        }
      });
  }

  ngOnDestroy(): void {
    this.seoService.removeCanonicalUrl();
    this.seoService.removeStructuredData();
    this.productService.unloadProduct();
    this.unsubscribe.next(undefined);
    this.unsubscribe.complete();
  }

  public async selectVariant(product: IShopifyProduct, index: number): Promise<void> {
    this.selectedSubscription = '';
    const urlParts = product.variants[index]?.id?.split('/');
    const variantId = urlParts[urlParts.length - 1];
    if (this.variantIndex !== index) {
      this.productService.unloadProduct();
      await this.router.navigate([`/products/${this.handle}`, variantId]);
    }
    this.variantIndex = index;
    this.changeDetectorRef.detectChanges();
    this.seo.image = product.variants[index].image.url;
    const metaDescription = product.descriptionHtml.substring(
      product.descriptionHtml.indexOf('<p>') + 3,
      product.descriptionHtml.indexOf('</p>')
    );
    this.seo.metaDescription = metaDescription.replace(/<\/?[^>]+(>|$)/g, '');

    if (product.seo.description) {
      this.seo.metaDescription = product.seo.description;
    }
    this.seo.metaTitle = product.title;
    this.seoService.setMetaData(this.seo);
    this.seoService.createProductStructuredData(product);
  }

  public add(variant: IShopifyProductVariant): void {
    if (variant.inventory > this.amountToAddToCart) {
      this.amountToAddToCart += 1;
    }
  }

  public remove(): void {
    if (this.amountToAddToCart > 1) {
      this.amountToAddToCart -= 1;
    }
  }

  public addProductToCart(variant: IShopifyProductVariant): void {
    this.cartService.insertCartItem(variant, this.amountToAddToCart, this.selectedSubscription);
    this.cartService.openDialog();
    this.googleAnalyticsService.itemAddedToCartConversion(variant?.price);
    this.googleAnalyticsService.eventEmitter('Added Product to Cart', 'Shopping Cart', 'Added Product to Cart', 'Shopping Cart', 1);
  }

  public selectedSubscriptionEvent(event: any): void {
    this.selectedSubscription = event;
  }

  private calculateCartAmount(): void {
    this.amountInCart = 0;
    const cartLines = this.cart?.lines?.filter(item => item.variant.id === this.product?.variants[this.variantIndex]?.id);

    if (cartLines && cartLines.length > 0) {
      cartLines.forEach((line: ICartLineInput) => {
        this.amountInCart += line.quantity;
      });
    }
  }
}
